import { useEffect, useState } from "react";
import { LogOut, Download, Lock, Phone, Mail, MapPin, CalendarDays, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("tt_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/enquiries?limit=500`, { headers: { "X-Admin-Token": t } });
      if (res.status === 401) { setError("Invalid admin token — please try again."); setAuthed(false); sessionStorage.removeItem("tt_admin_token"); return; }
      if (res.status === 503) { setError("Admin access is not configured on the server yet (ADMIN_TOKEN secret missing)."); setAuthed(false); return; }
      if (!res.ok) { setError("Could not load enquiries. Please retry."); setAuthed(false); return; }
      const data = await res.json();
      setRows(data.enquiries || []);
      setTotal(data.total || 0);
      setAuthed(true);
      sessionStorage.setItem("tt_admin_token", t);
    } catch (e) {
      setError("Network error — could not reach the server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("tt_admin_token");
    if (saved) load(saved);
  }, []);

  const csv = () => {
    const head = ["Received", "Name", "Phone", "Email", "Pickup city", "Destination", "Travel date", "Passengers", "Trip type", "Days", "Stops", "Requirements", "Source"];
    const lines = [head.join(",")];
    rows.forEach((r) => {
      const cells = [fmt(r.created_at), r.name, r.phone, r.email || "", r.pickup_city, r.destination, r.travel_date || "", r.passengers || "", r.trip_type || "", r.days || "", r.stops || "", r.requirements || "", r.source || ""];
      lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `tingadya-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 pt-24">
        <div className="gold-ring w-full max-w-md rounded-3xl border border-gold/40 bg-coal p-8" data-testid="admin-login-card">
          <Lock size={26} className="text-gold" />
          <h1 className="mt-4 font-display text-2xl font-bold text-white">Enquiries Admin</h1>
          <p className="mt-2 text-sm text-mist">Enter the admin token to view customer enquiries.</p>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && token && load(token)}
            placeholder="Admin token"
            data-testid="admin-token-input"
            className="mt-5 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-base text-snow placeholder:text-slategray outline-none focus:border-gold/60"
          />
          {error && <p className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-error-message">{error}</p>}
          <button onClick={() => token && load(token)} disabled={loading || !token} data-testid="admin-login-button" className="btn-gold mt-5 w-full disabled:opacity-50">
            {loading ? "Checking…" : "View Enquiries"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24 pt-24 sm:pt-28">
      <div className="container-x">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Tingadya Travels</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-white">Customer Enquiries</h1>
            <p className="mt-1 text-sm text-slategray" data-testid="admin-total-count">{total} total · newest first</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => load(token)} data-testid="admin-refresh-button" className="btn-ghost-gold"><RefreshCw size={15} /> Refresh</button>
            <button onClick={csv} data-testid="admin-csv-button" className="btn-ghost-gold"><Download size={15} /> CSV</button>
            <button onClick={() => { sessionStorage.removeItem("tt_admin_token"); setAuthed(false); setRows([]); }} data-testid="admin-logout-button" className="btn-ghost-gold"><LogOut size={15} /> Log out</button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-white/10 bg-coal p-12 text-center" data-testid="admin-empty-state">
            <p className="font-display text-xl text-white">No enquiries yet</p>
            <p className="mt-2 text-sm text-mist">New booking enquiries from the website will appear here instantly.</p>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-enquiries-table">
            <table className="w-full min-w-[1100px] border-collapse text-sm">
              <thead>
                <tr className="bg-coal2 text-left text-xs uppercase tracking-wider text-gold">
                  {["Received", "Name", "Phone", "Email", "Pickup", "Destination", "Travel date", "Pax", "Trip", "Days", "Stops / Notes", "Source"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-white/5 align-top text-snow/90 hover:bg-white/[0.03]" data-testid={`admin-enquiry-row-${i}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-mist">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-white">{r.name}</td>
                    <td className="whitespace-nowrap px-4 py-3"><a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 text-gold hover:underline"><Phone size={12} />{r.phone}</a></td>
                    <td className="px-4 py-3">{r.email ? <a href={`mailto:${r.email}`} className="text-gold hover:underline">{r.email}</a> : <span className="text-slategray">—</span>}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-gold/70" />{r.pickup_city}</span></td>
                    <td className="px-4 py-3 font-medium text-white">{r.destination}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className="inline-flex items-center gap-1.5"><CalendarDays size={12} className="text-gold/70" />{r.travel_date || "Flexible"}</span></td>
                    <td className="px-4 py-3">{r.passengers || "—"}</td>
                    <td className="px-4 py-3">{r.trip_type || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-3">{r.days || "—"}</td>
                    <td className="max-w-[260px] px-4 py-3 text-mist">{[r.stops, r.requirements].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slategray">{r.source || "website"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-xs text-slategray">
          Enquiries from the live website are stored in the production database. Preview and production databases are separate — this page shows the database of the site you are viewing.
        </p>
      </div>
    </div>
  );
}
